import Head from 'next/head'
import styled from 'styled-components'
import { useContext, useEffect, useReducer, useState } from 'react'
import { union, xor } from 'lodash'

import Tile from 'components/Tile'
import StateContext, { Holding } from 'components/StateProvider'
import Link from 'components/Next/Link'
import Tags from 'components/Tags'
import React from 'react'
import AnimatedGrid from 'components/Util/AnimatedGrid'
import Loading from 'components/Util/Loading'
import Spacer from 'components/Util/Spacer'
import Select from 'components/Input/Select'
import FlexGap from '@components/Util/FlexGap'

export default function Home({ }) {
  const state = useContext(StateContext)

  const sectorDiversification = state.sectorDiversification

  const [portfolio, setPortfolio] = useState([] as Holding[])

  useEffect(() => {
    setPortfolio(state.portfolio)
    const sectors = state.portfolio.map(holding => holding.Sector)
    setTags(Array.from(new Set(sectors)))
  }, [state.portfolio])

  const [sortedPortfolio, setSortedPortfolio] = useState([])

  // filter by tag
  const [activeTags, setActiveTags] = useState([])
  const [tags, setTags] = useState([])

  const [sortBy, setSortBy] = useState('Alphabetical')

  useEffect(() => {
    const filteredHoldings = portfolio.filter(holding => activeTags.includes(holding.Sector))

    const filtered = filteredHoldings.length === 0 ? portfolio : filteredHoldings   

    let sorted = []

    const cash = filtered.filter(holding => holding?.Name === 'Cash')[0]
    const cashIsUndefined = typeof cash === 'undefined'
    const filteredCashExcluded = filtered.filter(holding => holding?.Name !== 'Cash')

    const addCash = array => {
      return !cashIsUndefined 
        ? [
            ...array,
            cash
          ]
        : array
    }

    switch(sortBy) {
      case 'Alphabetical':
        sorted = addCash(filteredCashExcluded.sort((a, b) => a.Name.trim().localeCompare(b.Name.trim())))
        break
      case 'Weight':
        sorted = filtered.sort((a, b) => parseFloat(b['% Portfolio']) - parseFloat(a['% Portfolio']))
        break
      case '% Gain':
        sorted = addCash(filteredCashExcluded.sort((a, b) => parseFloat(b['% Gain']) - parseFloat(a['% Gain'])))
        break
      case 'Years Owned':
        sorted = addCash(filteredCashExcluded.sort((a, b) => parseFloat(b['Years Owned']) - parseFloat(a['Years Owned'])))
        break
      case 'Annualized Return':
        sorted = addCash(filteredCashExcluded.sort((a, b) => parseFloat(b['Annualized Return']) - parseFloat(a['Annualized Return'])))
        break
    }

    setSortedPortfolio([...sorted])

  }, [portfolio, activeTags, sortBy])
  
  const loading = sortedPortfolio.length === 0

  const activeSector = activeTags.length === 1 ? activeTags[0] : null

  const renderSectorDiversification = () => {
    const sectorData = sectorDiversification[activeTags[0]]
    const value = sectorData?.['Sector $']
    const weight = sectorData?.['Sector Weight']
    const targetWeight = sectorData?.['Benchmark']
    const overUnder = sectorData?.['Over/(Under)']
    return (
      <S_SectorInfoContainer>
        <FlexGap>
          <S_SectorInfo>
            Value:<S_Monospace>{value}</S_Monospace>
            &nbsp;&nbsp;&nbsp;
          </S_SectorInfo>

          <S_SectorInfo>
            Weight:<S_Monospace>{weight}</S_Monospace>
            &nbsp;&nbsp;&nbsp;
          </S_SectorInfo>

          <S_SectorInfo>
            Target Weight:<S_Monospace>{targetWeight}</S_Monospace>
            &nbsp;&nbsp;&nbsp;
          </S_SectorInfo>

          {
            overUnder
              ? <S_SectorInfo>
                  Over/Under:<S_Monospace>{overUnder}</S_Monospace>
                </S_SectorInfo>
              : null
          }
        </FlexGap>
      </S_SectorInfoContainer>
    )
  }
  
  const renderHoldings = () => (
    <S_Main>
      <Tags 
        tags={tags} 
        activeTags={activeTags}
        onePerLine={false}
        setActiveTag={tag => 
          setActiveTags(activeTags.includes(tag) 
            ? xor(activeTags, [tag]) 
            :  union(activeTags, [tag])
          )  
        } 
      />

      <S_Sort>
        <S_Results>{ `${sortedPortfolio.length} Holdings` } </S_Results>
        <Spacer />
        <Select
          icon='sort-amount-down'
          value={sortBy}
          onChangeFunction={e => setSortBy(e.target.value)}
          title='Sort by'
          id='sort'
          placeholder={'Sort by...'}
          options={[
            'Alphabetical',
            'Weight',
            '% Gain',
            'Years Owned',
            'Annualized Return'
          ]}
        />
      </S_Sort>

      <>
        {
            activeTags.length === 1
              ? <S_MobileSectorContainer>
                  {
                    renderSectorDiversification()
                  }
                </S_MobileSectorContainer>
              : null
          }
      </>

      <AnimatedGrid maxWidth={150}>
        {
          sortedPortfolio.map((holding, index) => 
            <Link href={holding?.Name === 'Cash' ? '/' : `/holdings/${holding?.Symbol}`}>
              <Tile
                key={holding?.Symbol + index}
                data={holding}
              />
            </Link>
          ) 
        }
      </AnimatedGrid>
    </S_Main>
  )

  const renderLoading = () => (
    <S_LoadingContainer>
      <Loading />
    </S_LoadingContainer>
  )

  return (
    <S_Container>
      <Head>
        <title>Portfolio - SIMM</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {
        loading
          ? renderLoading()
          : renderHoldings()
      }
    </S_Container>
  )
}

const S_Container = styled.div`
  width: 100%;
`

const S_Main = styled.main`
  padding: .5rem 0;
  color: var(--Font_Color);
`

const S_LoadingContainer = styled.div`
  width: 100%;
  height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
`

const S_Sort = styled.div`
  display: flex;
  width: calc(100% - 2rem);
  align-items: center;
  height: var(--Header_Height);
  padding: 0 1rem;
  margin-top: .5rem;
  margin-bottom: -.5rem;
`

const S_Results = styled.div`
  font-size: var(--Font_Size_Title);
  color: var(--Font_Color);
`

const S_SectorInfoContainer = styled.div`
  width: calc(100% - 2rem);
  padding: 0 1rem;
  display: flex;
  text-align: center;
  align-items: center;
  flex-wrap: wrap;
`

const S_SectorInfo = styled.div`
  display: flex;
  text-align: center;
`

const S_MobileSectorContainer = styled.div`
  display: flex;
  margin-top: 1rem;
  padding: 1rem 0;
  width: 100%;
  justify-content: center;
  box-shadow: var(--Neuo);
  flex-wrap: wrap;
`

const S_Monospace = styled.div`
  display: flex;
  font-family: monospace;
  line-height: 0;
  align-items: center;
  margin-left: .5rem;
  font-size: 16px;
`